import { z } from 'zod';
const uuid = z.string().uuid();
const dateTime = z.string().datetime({ offset: true });
export const notificationStateSchema = z.enum([
    'new',
    'read',
    'completed',
    'muted',
    'expired',
]);
export const notificationPrioritySchema = z.enum([
    'low',
    'normal',
    'high',
    'urgent',
]);
export const notificationChannelSchema = z.enum([
    'in_app',
    'email',
    'sms',
    'push',
]);
/** A domain-owned target reference; it is never a client supplied e-mail list. */
export const notificationAudienceSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('person'), personId: uuid }),
    z.object({ kind: z.literal('canteen_consumer'), consumerId: uuid }),
    z.object({ kind: z.literal('canteen_case_participants'), caseId: uuid }),
    z.object({ kind: z.literal('role'), roleCode: z.string().min(1).max(100) }),
    z.object({ kind: z.literal('group'), groupId: uuid }),
]);
export const notificationTemplateDataSchema = z.record(z.string().min(1).max(80), z.union([z.string().max(500), z.number(), z.boolean(), z.null()]));
export const notificationIntentSchema = z.object({
    contractVersion: z.literal('v1'),
    intentId: uuid,
    idempotencyKey: z.string().min(16).max(200),
    source: z.string().min(3).max(100),
    eventType: z.string().min(3).max(160),
    institutionId: uuid,
    aggregate: z.object({
        type: z.string().min(1).max(100),
        id: uuid,
        version: z.number().int().positive(),
    }),
    audience: notificationAudienceSchema,
    priority: notificationPrioritySchema.default('normal'),
    template: z.object({
        code: z.string().min(1).max(120),
        version: z.number().int().positive(),
        data: notificationTemplateDataSchema.default({}),
    }),
    mandatory: z.boolean().default(false),
    digestEligible: z.boolean().default(false),
    expiresAt: dateTime.nullable().default(null),
    occurredAt: dateTime,
    correlationId: z.string().min(1).max(100),
    causationId: uuid.nullable().default(null),
});
export const notificationIntentReceiptSchema = z.object({
    intentId: uuid,
    state: z.enum(['accepted', 'duplicate']),
    acceptedAt: dateTime,
});
export const notificationChangeSchema = z.object({
    cursor: z.string().min(1),
    id: uuid,
    state: notificationStateSchema,
    title: z.string().max(160),
    body: z.string().max(1000),
    priority: notificationPrioritySchema,
    actionUrl: z.string().max(500).nullable(),
    createdAt: dateTime,
    expiresAt: dateTime.nullable(),
});
export const notificationChangeFeedSchema = z.object({
    changes: z.array(notificationChangeSchema),
    nextCursor: z.string().min(1),
});
export const notificationDeliveryEventSchema = z.object({
    eventId: uuid,
    schemaVersion: z.literal(1),
    eventType: z.literal('notifications.delivery.updated.v1'),
    institutionId: uuid,
    notificationId: uuid,
    recipientId: uuid,
    channel: notificationChannelSchema,
    status: z.enum(['queued', 'delivered', 'failed', 'dead_letter']),
    occurredAt: dateTime,
    recordedAt: dateTime,
    correlationId: z.string().min(1).max(100),
    causationId: uuid.nullable(),
});
